// ============================================
// FILE: /frontend/src/components/facebook/TierGate.jsx
// PURPOSE: Block access for non-Gold/Platinum users
// Mobile-first styled using your guide patterns
// ============================================

import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, Crown, Sparkles, TrendingUp } from 'lucide-react';

export default function TierGate({ children, feature = 'Facebook Tools' }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const allowedTiers = ['GOLD', 'PLATINUM'];
  const userTier = user?.subscriptionTier?.toUpperCase();
  const hasAccess = userTier && allowedTiers.includes(userTier);

  console.log('[TierGate] 🔒 Checking access:', {
    userTier,
    hasAccess,
    feature
  });

  if (hasAccess) {
    console.log('[TierGate] ✅ Access granted');
    return <>{children}</>;
  }

  console.log('[TierGate] ⛔ Access denied - showing upgrade prompt');

  return (
    <div className="max-w-2xl mx-auto mt-6 sm:mt-8 md:mt-12 px-4">
      
      {/* Upgrade Alert */}
      <Alert className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 mb-6 sm:mb-8">
        <AlertCircle className="h-5 w-5 text-amber-600" />
        <AlertDescription className="text-xs sm:text-sm text-amber-900">
          <strong className="font-bold">Premium Feature:</strong> {feature} is available for Gold and Platinum members only.
        </AlertDescription>
      </Alert>

      {/* Upgrade Card */}
      <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/20 rounded-lg p-4 sm:p-6 md:p-8 text-center shadow-lg">
        
        {/* Icon */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-4 sm:p-5 rounded-full">
            <Crown className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
        </div>

        {/* Heading */}
        <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-2 sm:mb-3">
          🔒 Upgrade to Access {feature}
        </h2>
        <p className="text-xs sm:text-sm md:text-base text-muted-foreground mb-6 sm:mb-8">
          Join our Gold or Platinum members and unlock powerful social sharing tools
        </p>

        {/* Benefits List */}
        <div className="bg-background/80 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 text-left">
          <p className="font-semibold text-sm sm:text-base mb-3 sm:mb-4 text-center">
            ✨ With Gold/Platinum you get:
          </p>
          <ul className="space-y-2 sm:space-y-3 max-w-md mx-auto">
            <li className="flex items-start gap-2 sm:gap-3">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm">
                Create professional Facebook posts with image collages
              </span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm">
                Share up to 6 listings at once in a stunning 2×3 grid
              </span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm">
                Track clicks and engagement from your posts
              </span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm">
                Reach thousands on Facebook Groups & Marketplace
              </span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm">
                Professional branding with your dealer name
              </span>
            </li>
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => navigate('/dashboard/upgrade')}
            size="lg"
            className="w-full sm:w-auto bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold text-sm sm:text-base h-11 sm:h-12"
          >
            <Crown className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Upgrade Now
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            size="lg"
            className="w-full sm:w-auto text-xs sm:text-sm h-11 sm:h-12"
          >
            Back to Dashboard
          </Button>
        </div>

        {/* Current Tier Badge */}
        {userTier && (
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-4 sm:mt-6">
            Your current plan: <span className="font-semibold capitalize">{userTier.toLowerCase()}</span>
          </p>
        )}
      </div>
    </div>
  );
}